import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import Moment from "react-moment";
import { ENDPOINT } from "../../services/api-endpoint";
import { getListById } from "../../services/services";
import AppLoader from "../../components/AppLoader";
import { usePageLoader } from "../../services/usePageLoader";
import "../trurecipe/trurecipe-pages.scss";
import "./mes-job-detail.scss";

type StepStatus = "Complete" | "In Progress" | "Pending";

export type JobWorkflowStepView = {
  stepIndex: number;
  title: string;
  subtitle?: string;
  machineName?: string;
  machineTypeName?: string;
  iotCode?: string;
  operatorName?: string;
  operatorSkills?: string;
  status: StepStatus;
  partsCount?: number;
  expectedStart?: string;
  expectedEnd?: string;
  actualStart?: string;
  actualEnd?: string;
};

export type JobDetailDto = {
  id: number;
  jobname: string | null;
  status: string | null;
  estimatedstartdate?: string | null;
  estimatedenddate?: string | null;
  partproduced?: string | null;
  totalparts?: string | null;
  scrap?: string | null;
  partname?: string | null;
  ordercode?: string | null;
  qualityPct?: number;
  workflowStepCompleted?: number;
  workflowStepTotal?: number;
  workflowSteps?: JobWorkflowStepView[];
};

function parseNum(v: unknown): number {
  const n = Number(String(v ?? "").replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
}

function statusBadgeClass(status: string | null | undefined): string {
  const s = String(status ?? "").toLowerCase();
  if (s === "open") return "tv-mes-job-detail__badge tv-mes-job-detail__badge--open";
  if (s === "in progress" || s === "in_progress")
    return "tv-mes-job-detail__badge tv-mes-job-detail__badge--progress";
  if (s === "complete" || s === "closed")
    return "tv-mes-job-detail__badge tv-mes-job-detail__badge--complete";
  if (s === "on hold" || s === "on_hold")
    return "tv-mes-job-detail__badge tv-mes-job-detail__badge--hold";
  return "tv-mes-job-detail__badge tv-mes-job-detail__badge--muted";
}

function stepRowIcon(status: StepStatus): string {
  if (status === "Complete") return "fa fa-check-circle tv-mes-job-detail__step-icon tv-mes-job-detail__step-icon--done";
  if (status === "In Progress")
    return "fa fa-clock-o tv-mes-job-detail__step-icon tv-mes-job-detail__step-icon--prog";
  return "fa fa-circle-o tv-mes-job-detail__step-icon tv-mes-job-detail__step-icon--pend";
}

function pipelineChipClass(status: StepStatus): string {
  if (status === "Complete") {
    return "tv-mes-job-detail__wf-pipeline-chip tv-mes-job-detail__wf-pipeline-chip--done";
  }
  if (status === "In Progress") {
    return "tv-mes-job-detail__wf-pipeline-chip tv-mes-job-detail__wf-pipeline-chip--prog";
  }
  return "tv-mes-job-detail__wf-pipeline-chip tv-mes-job-detail__wf-pipeline-chip--pend";
}

const MesJobDetail = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { isLoading, runWithLoading } = usePageLoader();
  const [job, setJob] = useState<JobDetailDto | null>(null);
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});

  const load = useCallback(async () => {
    const id = Number(jobId);
    if (!Number.isFinite(id) || id <= 0) {
      navigate("/mes-jobs", { replace: true });
      return;
    }
    await runWithLoading(async () => {
      const raw = await getListById(ENDPOINT.JOB_LIST_BY_ID, id);
      const row = Array.isArray(raw) ? (raw[0] as JobDetailDto | undefined) : (raw as JobDetailDto | null);
      if (!row || (row as { id?: unknown }).id == null) {
        setJob(null);
        return;
      }
      setJob(row as JobDetailDto);
    });
  }, [jobId, navigate, runWithLoading]);

  useEffect(() => {
    void load();
  }, [load, location.key]);

  const steps = job?.workflowSteps ?? [];
  const wfTotal = job?.workflowStepTotal ?? steps.length;
  const wfDone = job?.workflowStepCompleted ?? 0;

  const legend = useMemo(() => {
    let c = 0,
      p = 0,
      w = 0;
    for (const s of steps) {
      if (s.status === "Complete") c++;
      else if (s.status === "In Progress") p++;
      else w++;
    }
    return { c, p, w };
  }, [steps]);

  const toggleRow = (idx: number) => {
    setExpanded((prev) => ({ ...prev, [idx]: !prev[idx] }));
  };

  if (!jobId) {
    return null;
  }

  const total = parseNum(job?.totalparts);
  const produced = parseNum(job?.partproduced);
  const scrap = parseNum(job?.scrap);
  const q = job?.qualityPct ?? (total > 0 ? Math.round(((produced - scrap) / total) * 100) : 100);

  return (
    <div className="tv-mes-v2-page tv-mes-jobs-page">
      {isLoading && <AppLoader text="Loading job..." />}
      <div className="tv-mes-v2-shell tv-mes-job-detail">
        <button type="button" className="tv-mes-job-detail__back" onClick={() => navigate("/mes-jobs")}>
          <i className="fa fa-arrow-left" aria-hidden />
          <span>Back to Jobs</span>
        </button>

        {!job && !isLoading ? (
          <p className="tv-mes-job-detail__empty">Job not found.</p>
        ) : job ? (
          <>
            <header className="tv-mes-job-detail__header">
              <div className="tv-mes-job-detail__title-row">
                <h1 className="tv-mes-job-detail__title">{job.jobname ?? "Job"}</h1>
                <span className={statusBadgeClass(job.status)}>{job.status ?? "—"}</span>
              </div>
              <p className="tv-mes-job-detail__meta">
                <span>{job.partname ?? "—"}</span>
                <span className="tv-mes-job-detail__meta-dot" aria-hidden>
                  •
                </span>
                {job.ordercode ? (
                  <span className="tv-mes-job-detail__po">{job.ordercode}</span>
                ) : (
                  <span className="text-muted">No order</span>
                )}
                <span className="tv-mes-job-detail__meta-dot" aria-hidden>
                  •
                </span>
                {job.estimatedstartdate ? (
                  <Moment format="D MMM YYYY">{job.estimatedstartdate}</Moment>
                ) : (
                  "—"
                )}
                <span className="tv-mes-job-detail__meta-dash"> — </span>
                {job.estimatedenddate ? (
                  <Moment format="D MMM YYYY">{job.estimatedenddate}</Moment>
                ) : (
                  "—"
                )}
              </p>
            </header>

            <section className="tv-mes-job-detail__kpis" aria-label="Job metrics">
              <div className="tv-mes-job-detail__kpi">
                <div className="tv-mes-job-detail__kpi-val">{total}</div>
                <div className="tv-mes-job-detail__kpi-lbl">Total parts</div>
              </div>
              <div className="tv-mes-job-detail__kpi">
                <div className="tv-mes-job-detail__kpi-val">{produced}</div>
                <div className="tv-mes-job-detail__kpi-lbl">Produced</div>
              </div>
              <div className="tv-mes-job-detail__kpi">
                <div className="tv-mes-job-detail__kpi-val">{scrap}</div>
                <div className="tv-mes-job-detail__kpi-lbl">Scrap</div>
              </div>
              <div className="tv-mes-job-detail__kpi">
                <div className="tv-mes-job-detail__kpi-val">{q}%</div>
                <div className="tv-mes-job-detail__kpi-lbl">Quality</div>
              </div>
              <div className="tv-mes-job-detail__kpi">
                <div className="tv-mes-job-detail__kpi-val">
                  {wfDone}/{wfTotal || "—"}
                </div>
                <div className="tv-mes-job-detail__kpi-lbl">Workflow</div>
              </div>
            </section>

            <section className="tv-mes-job-detail__wf tv-mes-v2-panel">
              <div className="tv-mes-job-detail__wf-head">
                <h2 className="tv-mes-job-detail__wf-title">
                  <i className="fa fa-sitemap tv-mes-job-detail__wf-ico" aria-hidden />
                  Workflow — {wfTotal} steps
                </h2>
                <div className="tv-mes-job-detail__legend" aria-label="Workflow legend">
                  <span>
                    <i className="fa fa-circle tv-mes-job-detail__leg-dot tv-mes-job-detail__leg-dot--done" />{" "}
                    Complete ({legend.c})
                  </span>
                  <span>
                    <i className="fa fa-circle tv-mes-job-detail__leg-dot tv-mes-job-detail__leg-dot--prog" />{" "}
                    In Progress ({legend.p})
                  </span>
                  <span>
                    <i className="fa fa-circle tv-mes-job-detail__leg-dot tv-mes-job-detail__leg-dot--pend" />{" "}
                    Pending ({legend.w})
                  </span>
                </div>
              </div>
              {/* <div className="tv-mes-job-detail__wf-bar" role="img" aria-label="Workflow progress by step">
                {wfTotal > 0 ? (
                  steps.map((s, i) => (
                    <div
                      key={`${s.stepIndex}-${i}`}
                      className={
                        s.status === "Complete"
                          ? "tv-mes-job-detail__wf-seg tv-mes-job-detail__wf-seg--done"
                          : s.status === "In Progress"
                            ? "tv-mes-job-detail__wf-seg tv-mes-job-detail__wf-seg--prog"
                            : "tv-mes-job-detail__wf-seg tv-mes-job-detail__wf-seg--pend"
                      }
                      style={{ flex: 1 }}
                    />
                  ))
                ) : (
                  <div className="tv-mes-job-detail__wf-seg tv-mes-job-detail__wf-seg--pend" style={{ flex: 1 }} />
                )}
              </div> */}

              {steps.length > 0 ? (
                <div className="tv-mes-job-detail__wf-pipeline" aria-label="Workflow steps by name">
                  {steps.map((s, i) => (
                    <Fragment key={`pipe-${s.stepIndex}-${i}`}>
                      <button
                        type="button"
                        className={pipelineChipClass(s.status)}
                        title={s.title}
                        aria-expanded={Boolean(expanded[i])}
                        aria-label={`${s.title}, step ${s.stepIndex}`}
                        onClick={() => toggleRow(i)}
                      >
                        <span className="tv-mes-job-detail__wf-pipeline-label">{s.title}</span>
                      </button>
                      {i < steps.length - 1 ? (
                        <span className="tv-mes-job-detail__wf-pipeline-arrow" aria-hidden>
                          &gt;
                        </span>
                      ) : null}
                    </Fragment>
                  ))}
                </div>
              ) : (
                <p className="tv-mes-job-detail__wf-pipeline-empty">No workflow steps for this job.</p>
              )}

              <ul className="tv-mes-job-detail__steps">
                {steps.map((s, i) => {
                  const open = Boolean(expanded[i]);
                  const machineCell = s.machineName?.trim() ? s.machineName : "No machine";
                  const stClass =
                    s.status === "Complete"
                      ? "tv-mes-job-detail__step-badge tv-mes-job-detail__step-badge--done"
                      : s.status === "In Progress"
                        ? "tv-mes-job-detail__step-badge tv-mes-job-detail__step-badge--prog"
                        : "tv-mes-job-detail__step-badge tv-mes-job-detail__step-badge--pend";
                  return (
                    <li key={`${s.stepIndex}-${i}`} className="tv-mes-job-detail__step">
                      <button
                        type="button"
                        className="tv-mes-job-detail__step-trigger"
                        onClick={() => toggleRow(i)}
                        aria-expanded={open}
                      >
                        <span className="tv-mes-job-detail__step-left">
                          <i className={stepRowIcon(s.status)} aria-hidden />
                          <span className="tv-mes-job-detail__step-num">{s.stepIndex}</span>
                          <span className="tv-mes-job-detail__step-titles">
                            <span className="tv-mes-job-detail__step-name">{s.title}</span>
                            {s.subtitle ? (
                              <span className="tv-mes-job-detail__step-sub">{s.subtitle}</span>
                            ) : null}
                          </span>
                        </span>
                        <span className="tv-mes-job-detail__step-mid">
                          <span className={!s.machineName?.trim() ? "tv-mes-job-detail__nomachine" : ""}>
                            {machineCell}
                          </span>
                          <span>{s.operatorName ?? "—"}</span>
                        </span>
                        <span className={stClass}>{s.status}</span>
                        <i className={`fa fa-chevron-${open ? "up" : "down"} tv-mes-job-detail__chev`} aria-hidden />
                      </button>
                      {open ? (
                        <div className="tv-mes-job-detail__step-body">
                          <dl className="tv-mes-job-detail__grid">
                            <div>
                              <dt>Process</dt>
                              <dd>
                                {s.title}
                                {s.machineTypeName ? ` (${s.machineTypeName})` : ""}
                              </dd>
                            </div>
                            <div>
                              <dt>Machine</dt>
                              <dd>
                                {s.machineName?.trim()
                                  ? [
                                      s.machineName,
                                      s.machineTypeName ? `(${s.machineTypeName})` : null,
                                      s.iotCode ? s.iotCode : null,
                                    ]
                                      .filter(Boolean)
                                      .join(" ")
                                  : "—"}
                              </dd>
                            </div>
                            <div>
                              <dt>Operator</dt>
                              <dd>
                                {s.operatorName ?? "—"}
                                {s.operatorSkills ? ` (${s.operatorSkills})` : ""}
                              </dd>
                            </div>
                            <div>
                              <dt>Parts</dt>
                              <dd>{s.partsCount ?? total}</dd>
                            </div>
                            <div className="tv-mes-job-detail__grid-span2">
                              <dt>Schedule</dt>
                              <dd>
                                <span className="tv-mes-job-detail__sched">
                                  <strong>Expected start</strong>{" "}
                                  {s.expectedStart ? <Moment format="D MMM YYYY">{s.expectedStart}</Moment> : "—"}
                                  <span className="tv-mes-job-detail__meta-pad">|</span>
                                  <strong>Expected end</strong>{" "}
                                  {s.expectedEnd ? <Moment format="D MMM YYYY">{s.expectedEnd}</Moment> : "—"}
                                </span>
                                <span className="tv-mes-job-detail__sched tv-mes-job-detail__sched--second">
                                  <strong>Actual start</strong>{" "}
                                  {s.actualStart ? <Moment format="D MMM YYYY">{s.actualStart}</Moment> : "—"}
                                  <span className="tv-mes-job-detail__meta-pad">|</span>
                                  <strong>Actual end</strong>{" "}
                                  {s.actualEnd ? <Moment format="D MMM YYYY">{s.actualEnd}</Moment> : "—"}
                                </span>
                              </dd>
                            </div>
                          </dl>
                          <div className="tv-mes-job-detail__guided-wrap">
                            <Link
                              to={`/mes-jobs/${jobId}/guided/${s.stepIndex}`}
                              className="tv-mes-job-detail__guided-btn"
                            >
                              <i className="fa fa-eye" aria-hidden />
                              Open Guided View
                            </Link>
                          </div>
                        </div>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            </section>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default MesJobDetail;
